import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Preferences } from "@capacitor/preferences";
import { datadogRum } from "@datadog/browser-rum";
import { AFConstants, AppsFlyer } from "appsflyer-capacitor-plugin";
import axios from "axios";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { queryClient } from "pages/Root";

import Services from "../src/services/Services";
import { clearAcknowledgedPopupPromotionIds } from "../src/utils/functions";

import { getMultiPlatformPlatformByPlatformIdTheme } from "./api/methods";
import { useAppState } from "./contexts/AppContext";
import useCheckin from "./hooks/useCheckin";
import { SAVE_CONTACT_PATH } from "./modules/saveContact/saveContactRoute";
import { storage } from "./modules/storage";
import { APPSFLYER_DOMAIN, getReferralCodeFromUrl } from "./services/DeepLinkService";
import { isNativeMobileApp } from "./utils/platform.helpers";

type AppUrlListenerProps = {
  resetTheme: () => void;
};

// The launching link is a per-webview-load concept: consult getLaunchUrl once, even if
// the component remounts.
let coldStartLaunchUrlChecked = false;

const AppUrlListener: React.FC<AppUrlListenerProps> = ({ resetTheme }) => {
  const navigate = useNavigate();

  // Move the useCheckin hook to the top level
  const { checkinUser } = useCheckin();

  const clearPlatformSpecificState = () => {
    clearAcknowledgedPopupPromotionIds();
  };

  const signOut = async () => {
    clearPlatformSpecificState();
    await Services.auth.clearAll();
    storage.removeItem("locationConfirmed");
    datadogRum.clearUser();
  };

  const appState = useAppState();
  const fetchPlatformByDomain = async (domain: string) => {
    const resp = await axios.get(
      `${domain}/api/v1/multi-platform/platform/domain?domain=${
        domain.split("repeatmd.app")?.[0].split("https://")?.[1]?.split(".")?.[0] + ".repeatmd.app"
      }`,
    );
    return resp.data?.result?.platformId;
  };

  const getPlatformIdFromUrl = (url: string) => {
    return url.split("platformId=")?.[1];
  };

  const getPlatformId = (url: string) => {
    const platformIdFromUrl = getPlatformIdFromUrl(url);

    if (!platformIdFromUrl) {
      const domain = getDomainFromUrl(url);
      return fetchPlatformByDomain(domain);
    }
    return platformIdFromUrl;
  };

  const getDomainFromUrl = (url: string) => {
    const subdomainWithProtocol = url.split("repeatmd.app")?.[0];
    return subdomainWithProtocol + "repeatmd.app";
  };

  const setAppState = async (url: string, platformId: string) => {
    const domain = getDomainFromUrl(url);

    const referralCode = getReferralCodeFromUrl(url);

    // If the link takes the user to a different platform, we need to update the AppState
    if (domain !== storage.getItem("baseApiUrl") || storage.getItem("platformId") !== platformId) {
      console.warn("Setting the app state");
      appState.setPlatformId(platformId);
      appState.setReferralCode(referralCode);
      appState.setApiUrl(domain);
      queryClient.invalidateQueries({ refetchType: "none" }); // marks all queries as invalid
    }
  };

  const isUserAuthorized = async (platformId: string) => {
    if (await Services.auth.isAuthorized()) {
      const platforms = await Preferences.get({
        key: "platformDomainTokens",
      });

      const requiredPlatform = JSON.parse(platforms.value || "[]")?.find(
        (platform: { platformId: string }) => platform.platformId === platformId,
      );

      if (requiredPlatform) {
        await Services.auth.setToken(requiredPlatform.token);
      } else {
        console.info(`No required platform with id ${platformId} found for the user`);
        return false;
      }
    } else {
      console.info("User is not authorized");
      return false;
    }
    return true;
  };

  const navigateToScreenFromPath = (path: string) => {
    if (path.startsWith("/api/v1/visit/checkin")) {
      // a client can check in from the home page
      navigate("/home");
      return;
    }

    const excludedRoutePrefixes = ["/api/"];
    if (excludedRoutePrefixes.some((prefix) => path.startsWith(prefix))) {
      return;
    }
    navigate(path);
  };

  const handleDeepLink = async (platformId: string, url?: string, token?: string) => {
    console.warn("Handle deep link", platformId, url);
    if (!url) {
      return;
    }

    if (token) {
      const previousToken = await Services.auth.getToken();
      if (previousToken === token) {
        console.warn("Token already set, skipping");
        return;
      }
      await Services.auth.setToken(token);
    }

    if (url) {
      await setAppState(url, platformId);
      resetTheme();
      const { pathname, search } = new URL(url);
      navigateToScreenFromPath(pathname + search);
    }
    // Add a condition to ensure the current path isn't /signin
    const currentPath = window.location.pathname;
    if (!(await isUserAuthorized(platformId)) && currentPath !== "/signin") {
      console.warn("Signing out");
      await signOut();
      location.reload();
    }
  };

  const addAppsFlyerListener = () => {
    AppsFlyer.addListener(AFConstants.UDL_CALLBACK, async (res) => {
      console.warn("APPSFLYER: UDL_CALLBACK", res);
      if (res.status === "FOUND") {
        const platformId = res.deepLink.deep_link_value;
        const url = res.deepLink.deep_link_sub1;
        const token = res.deepLink.deep_link_sub2;
        handleDeepLink(platformId, url, token);
      } else if (res.status === "ERROR") {
        console.log("UDL error: " + res.error);
      }
    });

    AppsFlyer.addListener(AFConstants.CONVERSION_CALLBACK, (res: any) => {
      console.warn("APPSFLYER: CONVERSION_CALLBACK", res);
    });

    if (import.meta.env.VITE_APPSFLYER_DEV_KEY) {
      AppsFlyer.initSDK({
        devKey: import.meta.env.VITE_APPSFLYER_DEV_KEY,
        appID: "6448703360",
        isDebug: true,
        registerOnDeepLink: true,
      })
        .then(() => {
          console.warn("AppsFlyer initialized");
        })
        .catch((err) => {
          console.error("AppsFlyer error" + err);
        });
    }
  };

  useEffect(() => {
    // Clear api url and platform id if user is not logged in and app is closed,
    // BUT preserve context during onboarding flows (e.g., when switching to SMS
    // to get verification code). We detect active onboarding by checking for
    // saved onboarding state in storage.
    App.addListener("pause", async () => {
      if (!(await Services.auth.isAuthorized())) {
        console.info("User is not authorized");

        // Check if user is in the middle of onboarding flow
        const hasOnboardingState = !!storage.getItem("onboarding_context_state");
        if (hasOnboardingState) {
          // Preserve all context during onboarding - the user is likely
          // switching to SMS or another app temporarily.
          console.info("Preserving context during onboarding flow");
          return;
        }

        appState.clear();
        console.info("Cleared api url and platform id");
      }
    });

    if (isNativeMobileApp()) {
      try {
        addAppsFlyerListener();
      } catch (error) {
        console.log("AppsFlyer error" + error);
      }
    }

    let appUrlOpenDelivered = false;
    App.addListener("appUrlOpen", async (event: URLOpenListenerEvent) => {
      console.warn("appUrlOpen", event);
      appUrlOpenDelivered = true;
      const url = new URL(event.url);
      openAppFromUrl(url);
    });

    // Cold start: Android only fires appUrlOpen from onNewIntent (warm starts), so the
    // link that launched the app is only reachable via getLaunchUrl. iOS retains the
    // event for the listener above instead — skip the fallback once it has fired (T-2193).
    if (isNativeMobileApp() && !coldStartLaunchUrlChecked) {
      coldStartLaunchUrlChecked = true;
      App.getLaunchUrl()
        .then((launchUrl) => {
          if (!launchUrl?.url || appUrlOpenDelivered) return;
          console.warn("Handling cold-start launch url", launchUrl.url);
          openAppFromUrl(new URL(launchUrl.url));
        })
        .catch((error) => console.warn("getLaunchUrl failed", error));
    }

    // Listen for internal URL navigation requests (from openUrl function)
    const handleInternalUrlNavigation = (event: CustomEvent<string>) => {
      console.log("handleInternalUrlNavigation", event);
      const url = new URL(event.detail);
      openAppFromUrl(url);
    };
    globalThis.addEventListener(
      "internalUrlNavigation",
      handleInternalUrlNavigation as EventListener,
    );

    const isUrlAPlatformCode = (url: URL) => {
      return (
        url.pathname.startsWith("/api/v1/visit/checkin") ||
        url.pathname.startsWith("/api/checkIn/location/")
      );
    };

    const redirectToPlatformSignInPage = async (url: URL, platformId: string) => {
      const params = new URLSearchParams({
        platformId,
      });

      const locationIdFromQuery = url.searchParams.get("locationId") ?? undefined;
      const locationId = locationIdFromQuery ?? url.pathname.split("/")[5];
      const referralCode = getReferralCodeFromUrl(url.toString());
      if (locationId) {
        params.append("locationId", locationId);
      }

      if (referralCode) {
        params.append("referralCode", referralCode);
      }

      if (isUrlAPlatformCode(url)) {
        storage.setItem("scannedCode", url.toString());
      }

      try {
        const platformTheme = await getMultiPlatformPlatformByPlatformIdTheme({
          platformId,
        });

        if (platformTheme?.logoImageUrl) {
          params.append("platformImageUrl", platformTheme.logoImageUrl);
        }
      } catch (error) {
        throw new Error("Get platform theme request failed.");
      }

      const redirectUrl = `/signin?${params.toString()}`;

      navigate(redirectUrl);
    };

    const openAppFromUrl = async (url: URL) => {
      const urlsToExclude = [APPSFLYER_DOMAIN, "//google/link/"];
      if (urlsToExclude.some((urlToExclude) => url.toString().includes(urlToExclude))) {
        console.warn("Skipping appUrlOpen");
        return;
      }

      // Check if URL should open in browser instead of app. The *.repeatmd.app link claim
      // also captures staff admin hosts (admin.repeatmd.app, admin.dev.repeatmd.app, ...),
      // which must always bounce to a browser (T-2193).
      const browserOnlyUrls = ["roadmap.repeatmd.app"];
      const isAdminHost =
        url.hostname.startsWith("admin.") && url.hostname.endsWith(".repeatmd.app");
      if (isAdminHost || browserOnlyUrls.some((browserUrl) => url.hostname === browserUrl)) {
        console.warn(`Opening ${url.hostname} in browser instead of app`);
        await Browser.open({ url: url.toString() });
        return;
      }

      const platformId = await getPlatformId(url.toString());
      if (!platformId) {
        console.warn("No platformId found");
        return;
      }

      const isUserLoggedIn = await Services.auth.isAuthorized();

      if (!isUserLoggedIn) {
        // Some pages are intentionally public and should work while signed out. The save-contact
        // page is texted to patients, so a phone with the app installed opens it here.
        if (url.pathname.startsWith("/referral-rewards/") || url.pathname === SAVE_CONTACT_PATH) {
          await setAppState(url.toString(), platformId);
          resetTheme();
          navigateToScreenFromPath(url.pathname + url.search);
          return;
        }
        // User is not logged in => navigate to the platform's sign in page
        await redirectToPlatformSignInPage(url, platformId);
      } else {
        const currentPlatformId = storage.getItem("platformId");
        const isUserLoggedInOnThePlatform = currentPlatformId === platformId;

        if (isUserLoggedInOnThePlatform) {
          // User is already logged in on the platform from the URL
          if (isUrlAPlatformCode(url)) {
            checkinUser(url.toString());
          } else {
            navigateToScreenFromPath(url.pathname + url.search);
          }
        } else {
          // User is logged in on a different platform
          const userHasAnAccountForThePlatform = await isUserAuthorized(platformId);
          if (userHasAnAccountForThePlatform) {
            // User has an account for the platform from the URL => navigate to the platform home screen
            await setAppState(url.toString(), platformId);
            resetTheme();
            if (isUrlAPlatformCode(url)) {
              storage.setItem("scannedCode", url.toString());
            }
            navigate("/home");
          } else {
            // User does not have an account for the platform from the URL => navigate to the platform sign in page
            await signOut();
            await redirectToPlatformSignInPage(url, platformId);
          }
        }
      }
    };

    return () => {
      App.removeAllListeners();
      window.removeEventListener(
        "internalUrlNavigation",
        handleInternalUrlNavigation as EventListener,
      );
    };
  }, []);

  return null;
};

export default AppUrlListener;
